import { ApiError, get, post } from '../api.js';
import {
  button, clear, el, field, input, panel, select, skeleton, submitButton,
} from '../ui.js';
import { STATES } from '../../../../packages/core/src/states.js';
import { ORG_ROLES, ORG_ROLE_LABEL, ORG_ROLE_BLURB } from '../../../../packages/core/src/rbac.js';

/**
 * The onboarding form an agency fills in from a link.
 *
 * The person filling this in has never seen Cellvora, is doing it once, and is
 * probably doing it because somebody asked them to. So: one page, plain words,
 * no jargon, and every field says what it is for in the language an agency
 * owner already uses. "Licence expiry", not "credential validity window".
 *
 * Rows are added one at a time rather than presented as a grid of twenty empty
 * lines. An agency with one licensed producer should see one row, not a form
 * that looks like it was built for somebody bigger and makes them feel behind.
 *
 * Nothing here reads anything back. The form writes into one agency and shows
 * only that agency's name, so a leaked link exposes nothing.
 */

interface Opened { agencyName: string; timezone: string }

interface Problem { tab: string; row: number; field: string; level: string; message: string }
interface Submitted {
  applied: boolean;
  agency: string | null;
  people: number; licences: number; sources: number; dnc: number;
  problems: Problem[];
  result?: { peopleCreated: number; licencesCreated: number; teamsCreated: number };
}

/**
 * Every place a producer can be licensed, by its whole name, in the order a
 * person expects to find it.
 *
 * Taken from the platform's own state reference rather than a list copied into
 * this file. The copy is how Puerto Rico came to be missing from the form
 * while the rest of the system knew about it, and how the form's list and the
 * system's list were free to drift apart in the first place.
 *
 * Sorted here, explicitly, because the source array is ordered inconsistently —
 * by name at the start and by postal code at the end — and a form is the wrong
 * place to discover that.
 */
const PLACES: Array<{ code: string; name: string }> = STATES
  .map((s) => ({ code: s.code, name: s.name }))
  .sort((a, b) => a.name.localeCompare(b.name, 'en'));

/** `[code, whole name]`, for every dropdown that asks for one state. */
const PLACE_OPTIONS: Array<[string, string]> = PLACES.map((p) => [p.code, p.name]);

const ZONES: Array<[string, string]> = [
  ['America/New_York', 'Eastern'],
  ['America/Chicago', 'Central'],
  ['America/Denver', 'Mountain'],
  ['America/Phoenix', 'Arizona (no daylight saving)'],
  ['America/Los_Angeles', 'Pacific'],
  ['America/Anchorage', 'Alaska'],
  ['Pacific/Honolulu', 'Hawaii'],
  ['America/Puerto_Rico', 'Atlantic (Puerto Rico)'],
];

/** A repeating block of rows with one "add another" button under it. */
function repeater(
  addLabel: string, makeRow: () => { node: HTMLElement; read: () => string[] },
): { node: HTMLElement; rows: () => string[][] } {
  const list = el('div', { class: 'rep' });
  const made: Array<{ read: () => string[] }> = [];

  const addOne = (): void => {
    const r = makeRow();
    made.push(r);
    const wrap = el('div', { class: 'rep-row' }, r.node);
    // Only after the first: somebody should never be able to delete their way
    // to an empty form and then wonder where the fields went.
    if (made.length > 1) {
      wrap.append(button('Remove', 'ghost sm', () => {
        wrap.remove();
        const i = made.indexOf(r);
        if (i >= 0) made.splice(i, 1);
      }));
    }
    list.append(wrap);
  };
  addOne();

  const node = el('div', null, list,
    el('div', { style: 'margin-top:10px' }, button(addLabel, 'ghost sm', addOne)));
  return { node, rows: () => made.map((m) => m.read()) };
}

export function intakeView(token: string): HTMLElement {
  const host = el('div', { class: 'intake' });
  clear(host).append(el('div', { class: 'center' }, skeleton(4)));

  void (async () => {
    let opened: Opened;
    try {
      opened = await get<Opened>(`/v1/intake/${encodeURIComponent(token)}`);
    } catch (e) {
      clear(host).append(el('div', { class: 'center' },
        el('div', { class: 'warnbox' },
          el('b', null, 'This link does not work. '),
          e instanceof ApiError ? e.message : 'Ask whoever sent it for a new one.')));
      return;
    }
    clear(host).append(form(opened));
  })();

  function form(opened: Opened): HTMLElement {
    const wrap = el('div', { class: 'intake-wrap' });

    wrap.append(el('div', { class: 'vhead' },
      el('span', { class: 'kicker', text: 'Getting set up' }),
      el('h1', { text: `Tell us about ${opened.agencyName}` }),
      el('p', {
        class: 'lede',
        text: 'Six short sections. Ten minutes. Everything you enter goes straight into '
          + 'your own system — nobody has to retype it.',
      })));

    /* ---------------------------------------------------------- agency --- */
    const legalName = input({ required: true, placeholder: 'As registered, not the trading name' });
    const address = input({ placeholder: 'Street address' });
    const city = input({ placeholder: 'City' });
    const homeState = select(PLACE_OPTIONS, 'FL');
    const zip = input({ placeholder: 'ZIP', inputmode: 'numeric', maxlength: '10' });
    const phone = input({ type: 'tel', placeholder: 'The number people see when you call' });
    const timezone = select(ZONES, opened.timezone);

    /* ---- which states they write in ---- */
    const stateBoxes = new Map<string, HTMLInputElement>();
    const stateGrid = el('div', { class: 'state-grid' });
    const stateRows: Array<{ name: string; row: HTMLElement }> = [];

    for (const place of PLACES) {
      const box = input({ type: 'checkbox' }) as HTMLInputElement;
      stateBoxes.set(place.code, box);
      // The whole name, never the abbreviation. Somebody scanning for
      // "Mississippi" should not have to know it is MS, and should not have to
      // wonder whether MS, MO and MT are in the order they look like.
      const row = el('label', { class: 'state-pick' }, box, el('span', { text: place.name }));
      stateGrid.append(row);
      stateRows.push({ name: place.name.toLowerCase(), row });
    }

    const noMatch = el('div', { class: 'state-empty', hidden: true },
      'No state by that name. Clear the box to see all of them again.');
    stateGrid.append(noMatch);

    const count = el('span', { class: 'state-count', text: 'None selected' });
    const refreshCount = (): void => {
      const n = [...stateBoxes.values()].filter((b) => b.checked).length;
      count.textContent = n === 0 ? 'None selected'
        : `${n} selected`;
    };
    for (const box of stateBoxes.values()) box.addEventListener('change', refreshCount);

    const filter = input({ type: 'search', placeholder: 'Type to find a state' });
    filter.addEventListener('input', () => {
      const q = filter.value.trim().toLowerCase();
      let shown = 0;
      for (const { name, row } of stateRows) {
        const hit = !q || name.includes(q);
        row.hidden = !hit;
        if (hit) shown += 1;
      }
      noMatch.hidden = shown > 0;
    });

    const setAll = (on: boolean) => (): void => {
      for (const { name, row } of stateRows) {
        // Only what is currently visible, so "Select all" after a search does
        // what it looks like it does rather than quietly ticking all fifty-two.
        if (row.hidden) continue;
        const box = row.querySelector('input');
        if (box instanceof HTMLInputElement) box.checked = on;
        void name;
      }
      refreshCount();
    };

    const stateTools = el('div', { class: 'state-tools' },
      filter,
      button('Select all', 'ghost sm', setAll(true)),
      button('Clear', 'ghost sm', setAll(false)),
      count);

    wrap.append(panel('1. Your agency',
      field('Legal business name', legalName),
      field('Street address', address),
      el('div', { class: 'row2' }, field('City', city), field('State', homeState)),
      el('div', { class: 'row2' }, field('ZIP', zip), field('Main phone number', phone)),
      field('What time zone is the office in?', timezone),
      el('div', { class: 'fieldish' },
        el('span', { class: 'flabel', text: 'Which states do you write business in?' }),
        el('small', {
          text: 'Tick every one, including Puerto Rico and Washington DC if you write '
            + 'there. Each place has its own legal calling hours, and this is what tells '
            + 'us when each person may be called. Leaving one out means leads there are '
            + 'never worked.',
        }),
        stateTools,
        stateGrid)));

    /* ---------------------------------------------------------- people --- */
    const people = repeater('Add another person', () => {
      const first = input({ placeholder: 'First name' });
      const last = input({ placeholder: 'Last name' });
      const email = input({ type: 'email', placeholder: 'Work email' });
      const mobile = input({ type: 'tel', placeholder: 'Mobile' });
      // Same labels as everywhere else. "Agency owner" is deliberate: this form
      // is filled in by an agency about itself, and the person who runs that
      // agency is not the person who runs Cellvora.
      const role = select(
        ORG_ROLES.map((r) => [r, `${ORG_ROLE_LABEL[r]} — ${ORG_ROLE_BLURB[r]}`] as [string, string]),
        'agent',
      );
      const team = input({ placeholder: 'Team name (optional)' });
      return {
        node: el('div', null,
          el('div', { class: 'row2' }, field('First name', first), field('Last name', last)),
          el('div', { class: 'row2' }, field('Work email', email), field('Mobile', mobile)),
          el('div', { class: 'row2' }, field('What can they see?', role), field('Team', team))),
        read: () => [first.value, last.value, email.value, mobile.value, role.value, team.value, 'No'],
      };
    });

    wrap.append(panel('2. Who needs a login',
      el('p', { class: 'note-p' },
        'Everyone at your agency who will use Cellvora. Agency owners and administrators ',
        el('b', null, 'do not'),
        ' need an insurance licence — only people who actually work leads do.'),
      people.node));

    /* -------------------------------------------------------- licences --- */
    const licences = repeater('Add another licence', () => {
      const first = input({ placeholder: 'First name' });
      const last = input({ placeholder: 'Last name' });
      const st = select(PLACE_OPTIONS, 'FL');
      const num = input({ placeholder: 'Licence number' });
      const exp = input({ type: 'date' });
      return {
        node: el('div', null,
          el('div', { class: 'row2' }, field('First name', first), field('Last name', last)),
          el('div', { class: 'row3' },
            field('State', st), field('Licence number', num), field('Expires', exp))),
        read: () => [first.value, last.value, st.value, num.value, exp.value, ''],
      };
    });

    wrap.append(panel('3. Insurance licences',
      el('p', { class: 'note-p' },
        'One row per person per state. If somebody is licensed in three states, that is '
        + 'three rows. ',
        el('b', null, 'The expiry date matters: '),
        'we use it to warn you before a licence lapses, and to make sure nobody is sent '
        + 'a lead in a state they are not licensed in.'),
      licences.node));

    /* --------------------------------------------------------- sources --- */
    const sources = repeater('Add another lead source', () => {
      const name = input({ placeholder: 'Vendor or campaign name' });
      const kind = select([
        ['exclusive', 'Exclusive — only we get this lead'],
        ['shared', 'Shared — sold to several agencies'],
        ['aged', 'Aged — older leads'],
        ['owned', 'Our own — our website or ads'],
      ], 'shared');
      const cost = input({ inputmode: 'decimal', placeholder: 'e.g. 28.00' });
      const consent = select([['Yes', 'Yes'], ['No', 'No'], ['', 'Not sure']], '');
      return {
        node: el('div', null,
          el('div', { class: 'row2' }, field('Where the leads come from', name), field('What kind?', kind)),
          el('div', { class: 'row2' },
            field('Cost per lead ($)', cost),
            field('Does their contract promise the person agreed to be called?', consent))),
        read: () => [name.value, kind.value, cost.value, consent.value, ''],
      };
    });

    wrap.append(panel('4. Where your leads come from',
      el('p', { class: 'note-p' },
        'One entry per vendor or campaign. This is what lets you see later which ones are '
        + 'actually worth the money.'),
      sources.node));

    /* ------------------------------------------------------------- dnc --- */
    const dncBox = el('textarea', {
      class: 'inp',
      rows: '6',
      placeholder: '(305) 555-0199\n713-555-0140\n…',
    }) as HTMLTextAreaElement;

    wrap.append(panel('5. Anyone who asked you to stop calling',
      el('p', { class: 'note-p' },
        'One phone number per line. If this list is in a notebook or a spreadsheet, paste '
        + 'it in — this is the moment it stops depending on somebody remembering.'),
      field('Phone numbers', dncBox)));

    /* ------------------------------------------------------------ leads -- */
    wrap.append(panel('6. Your leads',
      el('p', { class: 'note-p' },
        'Send your lead export separately to whoever gave you this link. It is loaded '
        + 'inside Cellvora, where it shows exactly how many rows are usable and why any '
        + 'were rejected ',
        el('b', null, 'before'),
        ' anything is saved — and that is a judgement call somebody at your agency should '
        + 'make with their eyes on it, not a form.')));

    /* ----------------------------------------------------------- submit -- */
    const errBox = el('div');
    const send = submitButton('Send this to Cellvora', 'pri block');

    const formEl = el('form', {
      onsubmit: async (e: Event) => {
        e.preventDefault();
        clear(errBox);
        send.disabled = true;
        send.textContent = 'Sending…';
        try {
          const states = [...stateBoxes.entries()].filter(([, b]) => b.checked).map(([s]) => s);
          const res = await post<Submitted>(`/v1/intake/${encodeURIComponent(token)}`, {
            // Sent in the same shape the workbook produces, so a form and a
            // spreadsheet are read by exactly the same code and cannot ever
            // disagree about what a valid licence looks like.
            sheets: [
              {
                name: 'Agency',
                rows: [
                  ['Legal entity name', legalName.value],
                  ['Business street address', address.value],
                  ['City', city.value],
                  ['State', homeState.value],
                  ['ZIP', zip.value],
                  ['Main phone', phone.value],
                  ['Office time zone', timezone.value],
                  ['Every state you write in', states.join(', ')],
                ],
              },
              {
                name: 'People',
                rows: [
                  ['First name', 'Last name', 'Work email', 'Mobile', 'Role', 'Team', 'Team leader'],
                  ...people.rows().filter((r) => r.some((c) => c.trim())),
                ],
              },
              {
                name: 'Licences',
                rows: [
                  ['First name', 'Last name', 'State', 'Licence number', 'Expiry date', 'Producer NPN'],
                  ...licences.rows().filter((r) => r[3]?.trim()),
                ],
              },
              {
                name: 'Lead sources',
                rows: [
                  ['Source name', 'Type', 'Cost per lead', 'Does the contract warrant consent', 'Notes'],
                  ...sources.rows().filter((r) => r[0]?.trim()),
                ],
              },
              {
                name: 'Do-not-call',
                rows: [
                  ['Phone number', 'Name', 'Date they asked', 'How they asked', 'Notes'],
                  ...dncBox.value.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
                    .map((p) => [p, '', '', '', '']),
                ],
              },
            ],
          });
          clear(host).append(done(res, opened));
          window.scrollTo({ top: 0 });
        } catch (e2) {
          errBox.append(el('div', { class: 'warnbox' },
            e2 instanceof ApiError ? e2.message : 'Could not send that. Try again in a moment.'));
        } finally {
          send.disabled = false;
          send.textContent = 'Send this to Cellvora';
        }
      },
    }, wrap, errBox, el('div', { style: 'margin:22px 0 60px' }, send));

    return formEl;
  }

  /** What they see afterwards. Says what happened, and what to fix if anything. */
  function done(res: Submitted, opened: Opened): HTMLElement {
    const errors = res.problems.filter((p) => p.level === 'error');
    const warnings = res.problems.filter((p) => p.level === 'warning');
    const box = el('div', { class: 'intake-wrap' });

    if (!res.applied) {
      box.append(el('div', { class: 'vhead' },
        el('span', { class: 'kicker', text: 'Not sent yet' }),
        el('h1', { text: 'A few things need fixing first' })));
      box.append(panel(null,
        el('p', { class: 'note-p' },
          'Nothing was saved — it is all still here. Fix these and send it again:'),
        el('ul', null, ...errors.map((p) => el('li', null,
          el('b', null, `${p.tab}: `), p.message))),
        el('div', { style: 'margin-top:16px' },
          button('Back to the form', 'pri', () => { clear(host).append(intakeView(token)); }))));
      return box;
    }

    box.append(el('div', { class: 'vhead' },
      el('span', { class: 'kicker', text: 'Done' }),
      el('h1', { text: 'That is everything — thank you' })));

    box.append(panel(`${opened.agencyName} is set up`,
      el('div', { class: 'okbox' },
        el('b', null, 'Saved. '),
        'Everything you entered is in the system now. Nobody has to retype any of it.'),
      el('div', { class: 'tiles', style: 'margin-top:14px' },
        ...[
          [String(res.people), 'People'],
          [String(res.licences), 'Licences'],
          [String(res.sources), 'Lead sources'],
          [String(res.dnc), 'Do-not-call'],
        ].map(([v, k]) => el('div', { class: 'tile', style: '--accent:var(--aqua)' },
          el('div', { class: 'k', text: k }),
          el('div', { class: 'v', text: v }))))));

    if (warnings.length) {
      box.append(panel('Worth knowing',
        el('p', { class: 'note-p' },
          'None of these stopped anything being saved. They are things somebody should '
          + 'look at:'),
        el('ul', null, ...warnings.map((p) => el('li', null,
          el('b', null, `${p.tab}: `), p.message)))));
    }

    box.append(panel('What happens next',
      el('ul', null,
        el('li', null, 'Everyone you listed gets an invitation to set up their own password.'),
        el('li', null, 'Your lead file gets loaded, and you will see exactly what imported.'),
        el('li', null, 'After that you can close this page — you will not need this link again.'))));

    return box;
  }

  return host;
}
